<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import type { Field } from "@yawhub/core";
import { api, errorText } from "../api";
import ViewsPanel from "./ViewsPanel.vue";
const props = defineProps<{
  dataset: string;
  fields: Field[];
  views: any[];
  canWrite: boolean;
}>();
const emit = defineEmits(["changed", "selected"]),
  route = useRoute(),
  router = useRouter();
const dashboards = ref<any[]>([]),
  busy = ref(false),
  error = ref(""),
  editing = ref<"new" | "rename" | null>(null),
  name = ref("");
const current = computed(
  () =>
    dashboards.value.find((d) => d.id === route.query.dashboard) ??
    dashboards.value[0],
);
watch(
  () => current.value?.id,
  (id) => emit("selected", id),
  { immediate: true },
);
async function select(id: string) {
  await router.replace({
    query: { ...route.query, tab: "dashboard", dashboard: id },
  });
}
async function load() {
  try {
    dashboards.value = await api(`/api/datasets/${props.dataset}/dashboards`);
    error.value = "";
  } catch (e) {
    error.value = errorText(e);
  }
}
async function save() {
  busy.value = true;
  error.value = "";
  try {
    const row = await api(
      `/api/datasets/${props.dataset}/dashboards${editing.value === "rename" ? "/" + current.value.id : ""}`,
      {
        method: editing.value === "rename" ? "PUT" : "POST",
        revision:
          editing.value === "rename" ? current.value.revision : undefined,
        body: { name: name.value },
      },
    );
    await load();
    await select(row.id);
    editing.value = null;
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}
async function remove() {
  busy.value = true;
  error.value = "";
  try {
    await api(`/api/datasets/${props.dataset}/dashboards/${current.value.id}`, {
      method: "DELETE",
    });
    await load();
    if (current.value) await select(current.value.id);
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}
async function changed() {
  await load();
  emit("changed");
}
onMounted(load);
watch(() => props.views, load);
</script>
<template>
  <section class="dashboards-panel">
    <div class="dashboard-toolbar">
      <label v-if="dashboards.length"
        >Dashboard<select
          :value="current?.id"
          @change="select(($event.target as HTMLSelectElement).value)"
        >
          <option v-for="d in dashboards" :key="d.id" :value="d.id">
            {{ d.name }}
          </option>
        </select></label
      >
      <div v-if="canWrite" class="heading-actions">
        <button
          class="button secondary"
          :disabled="busy"
          @click="
            editing = 'new';
            name = '';
          "
        >
          Nyt dashboard
        </button>
        <button
          v-if="current"
          class="text-button"
          :disabled="busy"
          @click="
            editing = 'rename';
            name = current.name;
          "
        >
          Omdøb
        </button>
        <button
          v-if="current && !views.some((v) => v.dashboard_id === current.id)"
          class="text-button danger"
          :disabled="busy"
          @click="remove"
        >
          Fjern tomt dashboard
        </button>
      </div>
    </div>
    <form
      v-if="editing"
      class="inline-editor form-stack"
      @submit.prevent="save"
    >
      <label
        >Navn på dashboard<input
          v-model="name"
          required
          maxlength="120"
          autofocus
      /></label>
      <div class="form-actions">
        <button
          type="button"
          class="button secondary"
          :disabled="busy"
          @click="editing = null"
        >
          Annullér</button
        ><button class="button primary" :disabled="busy">
          {{ busy ? "Gemmer…" : "Gem dashboard" }}
        </button>
      </div>
    </form>
    <p v-if="error" class="error" role="alert">{{ error }}</p>
    <ViewsPanel
      v-if="current"
      :key="current.id"
      :dataset="dataset"
      :fields="fields"
      :views="views"
      :can-write="canWrite"
      mode="dashboard"
      :dashboard="current"
      :dashboards="dashboards"
      @changed="changed"
    />
    <div v-else-if="!editing" class="empty-state">
      <h2>Dit første dashboard</h2>
      <p>
        Saml grafer, tabeller og nøgletal i et dashboard med et navn, der giver
        mening for dig.
      </p>
    </div>
  </section>
</template>
<style
  scoped
  lang="scss"
  src="../styles/components/DashboardsPanel.scss"
></style>
